import {
  Button,
  ChakraProvider,
  CSSReset,
  extendTheme,
  Flex,
  Text,
} from '@chakra-ui/react';
import firebase from 'firebase';
import 'moment/locale/de';
import React, { useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import ReactPixel from 'react-facebook-pixel';
import { BrowserRouter as Router } from 'react-router-dom';
// eslint-disable-next-line import/no-extraneous-dependencies
import 'regenerator-runtime/runtime';
import { Routes } from './routes';
import { shadeColor } from './utils/shadeColor';

import '@fontsource/raleway/100.css';
import '@fontsource/raleway/200.css';
import '@fontsource/raleway/300.css';
import '@fontsource/raleway/400.css';
import '@fontsource/raleway/500.css';
import '@fontsource/raleway/600.css';
import '@fontsource/raleway/700.css';
import '@fontsource/raleway/800.css';
import '@fontsource/raleway/900.css';
import '@fontsource/pacifico';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_API_KEY,
  authDomain: process.env.REACT_APP_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_PROJECT_ID,
  storageBucket: process.env.REACT_APP_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER,
  appId: process.env.REACT_APP_APP_ID,
  measurementId: process.env.REACT_APP_MEASUREMENT_ID,
};

if (firebase.apps.length < 1) {
  const app = firebase.initializeApp(firebaseConfig);

  if (!process.env.REACT_APP_PRODSOURCE) {
    firebase.auth(app).useEmulator('http://localhost:9099');
    // eslint-disable-next-line no-restricted-globals
    firebase.firestore(app).useEmulator('localhost', 8080);
    firebase.storage(app).useEmulator('localhost', 9199);
    firebase.functions(app).useEmulator('localhost', 5001);
  }
}

export const App: React.FC = () => {
  const [color, setColor] = useState('#aaaaaa');
  const [pixelId, setPixelId] = useState('');
  const [cookies, setCookie] = useCookies();
  const [showCookie, setShowCookie] = useState(!cookies.privacyConsent);

  useEffect(() => {
    firebase
      .firestore()
      .collection('config')
      .doc('default')
      .get()
      .then((data) => {
        setColor(data.data()?.color ? data.data()?.color : color);
        setPixelId(data.data()?.pixelId ? data.data()?.pixelId : pixelId);
      });
  }, []);

  useEffect(() => {
    if (cookies.privacyConsent && pixelId) {
      ReactPixel.init(pixelId);
      ReactPixel.pageView();
    }
  }, [cookies, pixelId]);

  const theme = extendTheme({
    colors: {
      brand: {
        10: shadeColor(color, 200),
        100: shadeColor(color, 80),
        200: shadeColor(color, 60),
        300: shadeColor(color, 40),
        400: shadeColor(color, 20),
        500: shadeColor(color, 0),
        600: shadeColor(color, -20),
        700: shadeColor(color, -40),
        800: shadeColor(color, -60),
        900: shadeColor(color, -80),
      },
    },
    fonts: { body: "'Raleway',sans-serif", heading: "'Raleway',sans-serif" },
    defaultProps: {
      focusBorderColor: 'brand.700',
    },
    styles: {
      global: {
        'body, html': {
          overflowX: 'hidden',
          minHeight: '-webkit-fill-available',
        },
        '.rdtPicker table': {
          width: '100%',
          padding: '5px',
        },
        '.rdtNext,.rdtSwitch, .rdtPrev ': {
          padding: '8px 0',
          borderRadius: 'md',
          textAlign: 'center',
          cursor: 'pointer',
        },
        '.rdtDay, .rdtMonth, .rdtYear': {
          padding: '6px 10px',
          cursor: 'pointer',
          textAlign: 'center',
          borderRadius: 'md',
        },
        '.rdtToday': {
          color: 'brand.500',
          fontWeight: 'bold',
        },
        '.rdtPrev:hover, .rdtDay:hover, .rdtNext:hover, .rdtSwitch:hover, .rdtMonth:hover ':
          {
            background: 'gray.200',
          },
        '.rdtActive, .rdtActive:hover': {
          background: 'brand.500',
          color: '#fff',
        },
        '.rdtDisabled': {
          color: 'gray.400',
        },
        '.rdtDisabled:hover': {
          background: 'none',
          cursor: 'not-allowed',
        },
      },
    },
    config: {
      initialColorMode: 'light',
    },
    shadows: { outline: 'none' },
  });

  return (
    <ChakraProvider theme={theme}>
      {showCookie && (
        <Flex
          bg="white"
          zIndex="999"
          position="fixed"
          bottom={0}
          left={0}
          right={0}
          p={4}
          justify="center"
          align="center"
          direction={['column', 'column', 'row']}
        >
          <Text mr={[0, 0, 4]} mb={[2, 2, 0]}>
            Diese Webseite benutzt Cookies, um die Nutzererfahrung zu
            verbessern.
          </Text>
          <Flex>
            <Button variant="ghost" onClick={() => setShowCookie(false)} mr={2}>
              Ablehnen
            </Button>
            <Button
              variant="outline"
              colorScheme="brand"
              onClick={() => {
                setCookie('privacyConsent', true);
                setShowCookie(false);
              }}
            >
              Akzeptieren
            </Button>
          </Flex>
        </Flex>
      )}
      <CSSReset />
      <Router>
        <Routes />
      </Router>
    </ChakraProvider>
  );
};
